// The drawing's grouping rules, DOM-free (unit-tested in test/groups.test.mjs).
//
// ONE kind of group, PowerPoint's: `Drawing.groups` holds every group, each
// object points at its innermost group by `groupId`, and groups nest through
// `parent`. A click on a member selects the outermost group and each further
// click drills one level in (`clickTarget`). A placed multi-part product is a
// group with a `source` (its catalogue product); a repeat row is a plain group.
//
// What a part stands in is not stored: a room's contents are whatever stands in
// it (`hostRoomOf` in main.ts). A saved drawing from before that carried the
// room's contents as a shared `groupId` on the room and everything in it; the
// load-time migration below takes the room out of that group.

import type { MultiKind } from './selection';
import type {
  Drawing,
  DrawingGroup,
  ProductInstance,
} from '../shared/types';

/**
 * Take every room out of the group it used to share with its contents. A member
 * standing in the room (or a fixture of it) is the room's by position now, so it
 * leaves the group too — unless something OUTSIDE the room was related to it,
 * in which case the members outside and inside stay related (the group lives on
 * without the room). A room's fixtures never keep a group.
 * `inRoom` says whether an instance's centre is inside a room.
 */
export function migrateRoomGroups(d: Drawing, inRoom: (room: ProductInstance, part: ProductInstance) => boolean): void {
  const insts = d.instances || [];
  const roomGids = new Set(insts.filter((i) => i.autoBenches && i.groupId).map((i) => i.groupId!));
  const ungroup = (i: ProductInstance): void => { delete i.groupId; delete i.groupName; };
  for (const gid of roomGids) {
    const rooms = insts.filter((i) => i.groupId === gid && i.autoBenches);
    const members = insts.filter((i) => i.groupId === gid && !i.autoBenches);
    rooms.forEach(ungroup);
    members.filter((m) => m.roomFixture).forEach(ungroup);
    const parts = members.filter((m) => !m.roomFixture);
    const outside = parts.filter((m) => !rooms.some((r) => inRoom(r, m)));
    if (!outside.length || parts.length < 2) parts.forEach(ungroup);
  }
}

// ---- Related parts (`relId`) ----
// Dropping a part on a part relates them: related parts share one `relId`, and
// the relation is transitive (relating into a cluster joins all of it). It only
// decides what a part's Connections dropdown offers — never grouping, moving or
// copying — so it lives apart from `groupId` and from the room a part is in.

type Rel = { id: string; relId?: string };

/** Relate `a` and `b`, merging their clusters. `gen` mints a new cluster id. */
export function relate<T extends Rel>(insts: T[], a: T, b: T, gen: () => string): void {
  const id = a.relId ?? b.relId ?? gen();
  const merge = new Set([a.relId, b.relId].filter(Boolean));
  for (const i of insts) if (i.relId && merge.has(i.relId)) i.relId = id;
  a.relId = id;
  b.relId = id;
}

/** The parts related to `inst` (itself excluded). */
export function relatedTo<T extends Rel>(insts: T[], inst: T): T[] {
  return inst.relId ? insts.filter((i) => i !== inst && i.relId === inst.relId) : [];
}

/** Take the given parts out of their relations. */
export function unrelate<T extends Rel>(insts: T[], ids: ReadonlySet<string>): void {
  for (const i of insts) if (ids.has(i.id)) delete i.relId;
  pruneRelations(insts);
}

/** A part related to nothing else keeps no relation. */
export function pruneRelations<T extends Rel>(insts: T[]): void {
  const n = new Map<string, number>();
  for (const i of insts) if (i.relId) n.set(i.relId, (n.get(i.relId) ?? 0) + 1);
  for (const i of insts) if (i.relId && (n.get(i.relId) ?? 0) < 2) delete i.relId;
}

/**
 * Load-time: a saved "related parts" group (a `grp-` / `mp-` group id — never a
 * Ctrl+G `ug-` group or a `rep-` repeat row) becomes a relation. The parts of a
 * multi-part product in it (`isProductPart`) stay grouped as that product, and
 * stay related to the rest; everything else leaves the group.
 */
export function migrateRelations(d: Drawing, isProductPart: (i: ProductInstance) => boolean): void {
  const insts = d.instances || [];
  const gids = new Set(insts.map((i) => i.groupId).filter((g): g is string => !!g && !g.startsWith('ug-') && !g.startsWith('rep-')));
  for (const gid of gids) {
    const members = insts.filter((i) => i.groupId === gid);
    const product = members.filter(isProductPart);
    const others = members.filter((m) => !product.includes(m));
    if (!others.length) continue;
    for (const m of others) { delete m.groupId; delete m.groupName; }
    if (members.length > 1) for (const m of members) m.relId = gid;
  }
  pruneRelations(insts);
}

// ---- The group registry (`Drawing.groups`) ----

/** Anything a group can hold: every drawing object carries an id and a groupId. */
export type Groupable = { id: string; groupId?: string };

/** Every object of every groupable kind in the drawing (or a copy's payload). */
/** Where each kind a group can hold (the multi kinds, selection.ts) keeps its
 *  objects, in the order `groupables` lists them. A Record, so a new multi kind
 *  does not compile until it says. */
const GROUPABLE_ARRAYS = {
  instance: 'instances',
  polygon: 'polygons',
  wall: 'walls',
  text: 'texts',
  arc: 'arcs',
  leader: 'leaders',
  cloud: 'revisionClouds',
} as const satisfies Record<MultiKind, keyof Drawing>;

export function groupables(d: Partial<Pick<Drawing, (typeof GROUPABLE_ARRAYS)[MultiKind]>>): Groupable[] {
  return Object.values(GROUPABLE_ARRAYS).flatMap((k): Groupable[] => d[k] || []);
}

export function groupById(d: Drawing, id: string | undefined): DrawingGroup | undefined {
  return id ? (d.groups || []).find((g) => g.id === id) : undefined;
}

/** A group and every group it is nested in, innermost first (cycle-safe). */
export function groupChain(d: Drawing, gid: string | undefined): string[] {
  const out: string[] = [];
  for (let g = gid; g && !out.includes(g); g = groupById(d, g)?.parent) out.push(g);
  return out;
}

/** The outermost group an object belongs to. */
export function outermostGroup(d: Drawing, o: Groupable): string | undefined {
  return groupChain(d, o.groupId).at(-1);
}

/** Every object in a group, nested groups included. */
export function groupMembers(d: Drawing, gid: string): Groupable[] {
  return groupables(d).filter((o) => groupChain(d, o.groupId).includes(gid));
}

/** A name the app once stored for a group ("Group NN"), not one entered: a
 *  group now has no name until one is entered, its default shown instead
 *  (docs/claude/names.md). */
export function isAutoGroupName(name: string): boolean {
  return /^Group\s+\d+$/i.test(name);
}

/** A drawing saved when groups were named "Group NN" as text: those names
 *  go, so each shows its default again. Once, on load. */
export function settleGroupNames(d: Drawing): void {
  for (const g of d.groups ?? []) if (g.name && isAutoGroupName(g.name)) delete g.name;
}

/**
 * Make the registry match what the objects reference: a group an object points
 * at gets an entry, a nesting loop or a dangling parent is cut, and a group
 * holding nothing goes. A plain group left with fewer than two members (objects
 * or nested groups) dissolves into its parent; a product (`source`) keeps even
 * one part.
 */
export function normalizeGroups(d: Drawing): void {
  const reg = (d.groups ??= []);
  for (const o of groupables(d)) if (o.groupId && !reg.some((g) => g.id === o.groupId)) reg.push({ id: o.groupId });
  for (const g of reg) {
    if (g.parent && (!reg.some((p) => p.id === g.parent) || groupChain(d, g.parent).includes(g.id))) delete g.parent;
  }
  for (let changed = true; changed;) {
    changed = false;
    for (const g of [...reg]) {
      const objs = groupables(d).filter((o) => o.groupId === g.id);
      const kids = reg.filter((k) => k.parent === g.id);
      const n = objs.length + kids.length;
      if (n > 1 || (n === 1 && g.source)) continue;
      for (const o of objs) { if (g.parent) o.groupId = g.parent; else delete o.groupId; }
      for (const k of kids) { if (g.parent) k.parent = g.parent; else delete k.parent; }
      reg.splice(reg.indexOf(g), 1);
      changed = true;
    }
  }
  if (!reg.length) delete d.groups;
}

/** One thing a selection holds: a whole group, or an object on its own. */
type Unit = { group: string; container?: string } | { obj: Groupable; container?: string };

/** The selection as units: each object stands for its outermost group whose
 *  members are ALL selected, else for itself. `container` is the group the unit
 *  sits in. */
function selectionUnits(d: Drawing, selected: ReadonlySet<string>): Unit[] {
  const units: Unit[] = [];
  const seen = new Set<string>();
  const all = (gid: string): boolean => groupMembers(d, gid).every((m) => selected.has(m.id));
  for (const o of groupables(d)) {
    if (!selected.has(o.id)) continue;
    const chain = groupChain(d, o.groupId);
    const whole = [...chain].reverse().find(all);
    if (whole) {
      if (!seen.has(whole)) { seen.add(whole); units.push({ group: whole, container: groupById(d, whole)?.parent }); }
    } else {
      units.push({ obj: o, container: o.groupId });
    }
  }
  return units;
}

/**
 * Ctrl+G: the selected objects and whole groups become one new group, nested in
 * the group they all sit in (if any). Fails with `too-few` for fewer than two
 * units and `mixed` when they sit in different groups.
 */
export function groupSelected(d: Drawing, selected: ReadonlySet<string>, id: string, name?: string): 'ok' | 'too-few' | 'mixed' {
  const units = selectionUnits(d, selected);
  if (units.length < 2) return 'too-few';
  const container = units[0].container;
  if (units.some((u) => u.container !== container)) return 'mixed';
  (d.groups ??= []).push({ id, ...(name ? { name } : {}), ...(container ? { parent: container } : {}) });
  for (const u of units) {
    if ('group' in u) groupById(d, u.group)!.parent = id;
    else u.obj.groupId = id;
  }
  return 'ok';
}

/** Ctrl+Shift+G: every whole group the selection holds loses one level — its
 *  members move up into the group it sat in. Returns the groups dissolved. */
export function ungroupSelected(d: Drawing, selected: ReadonlySet<string>): DrawingGroup[] {
  const gone: DrawingGroup[] = [];
  for (const u of selectionUnits(d, selected)) {
    if (!('group' in u)) continue;
    const g = groupById(d, u.group)!;
    for (const o of groupables(d)) if (o.groupId === g.id) { if (g.parent) o.groupId = g.parent; else delete o.groupId; }
    for (const k of d.groups || []) if (k.parent === g.id) { if (g.parent) k.parent = g.parent; else delete k.parent; }
    d.groups = (d.groups || []).filter((x) => x !== g);
    gone.push(g);
  }
  normalizeGroups(d);
  return gone;
}

/**
 * What a plain click on object `id` selects. Not in a group → null (the click is
 * an ordinary one). Otherwise PowerPoint's rule: when the selection is not
 * already inside the object's outermost group, `now` is that whole group (and a
 * drag moves it); when the selection IS one of the object's groups, a click
 * without a drag drills one level in (`drill`) — to the next nested group, and
 * from the innermost one to the object itself. A press on what was drilled to
 * (the selection, inside that group) keeps it, so a drag moves it alone; a click
 * goes round again to the whole group.
 */
export function clickTarget(d: Drawing, id: string, selected: ReadonlySet<string>): { now?: string[]; drill?: string[] } | null {
  const o = groupables(d).find((x) => x.id === id);
  const chain = o ? [...groupChain(d, o.groupId)].reverse() : [];
  if (!chain.length) return null;
  const ids = (gid: string): string[] => groupMembers(d, gid).map((m) => m.id);
  const same = (a: string[]): boolean => a.length === selected.size && a.every((x) => selected.has(x));
  for (let i = chain.length - 1; i >= 0; i--) {
    if (same(ids(chain[i]))) return { drill: i + 1 < chain.length ? ids(chain[i + 1]) : [id] };
  }
  const outer = ids(chain[0]);
  if (outer.every((x) => selected.has(x))) return { drill: outer };
  if (selected.has(id) && [...selected].every((x) => outer.includes(x))) return { drill: outer };
  return { now: outer };
}

/**
 * The groups a copy of `copied` takes along: only those copied WHOLE (every
 * member in the copy), each nested in its nearest wholly-copied ancestor.
 * `groupOf` gives each copied object's group in the copy — its innermost
 * wholly-copied group — so a part copied out of a group lands on its own.
 */
export function groupsForCopy(d: Drawing, copied: ReadonlySet<string>): { groups: DrawingGroup[]; groupOf: Map<string, string | undefined> } {
  const whole = new Map<string, boolean>();
  const isWhole = (gid: string): boolean => {
    if (!whole.has(gid)) whole.set(gid, groupMembers(d, gid).every((m) => copied.has(m.id)));
    return whole.get(gid)!;
  };
  const groupOf = new Map<string, string | undefined>();
  const keep = new Set<string>();
  for (const o of groupables(d)) {
    if (!copied.has(o.id)) continue;
    const chain = groupChain(d, o.groupId).filter(isWhole);
    groupOf.set(o.id, chain[0]);
    chain.forEach((g) => keep.add(g));
  }
  const groups = [...keep].map((gid) => {
    const g = structuredClone(groupById(d, gid) ?? { id: gid });
    const parent = groupChain(d, g.parent).find(isWhole);
    if (parent) g.parent = parent; else delete g.parent;
    return g;
  });
  return { groups, groupOf };
}

/**
 * Load-time: build the registry from the ids the objects carry. A group's name
 * was stored on every member (`groupName`); it moves to the group's entry.
 */
export function migrateGroupRegistry(d: Drawing): void {
  const reg = (d.groups ??= []);
  for (const i of d.instances || []) {
    if (i.groupId && !reg.some((g) => g.id === i.groupId)) reg.push({ id: i.groupId });
    const g = reg.find((x) => x.id === i.groupId);
    if (g && i.groupName && !g.name) g.name = i.groupName;
    delete i.groupName;
  }
  normalizeGroups(d);
}

// ---- Products: groups linked to their catalogue product ----

/**
 * Match a placed product's parts to its catalogue parts: by `partId`, and — for
 * a part placed before parts carried one — by name. `matched` maps each catalogue
 * part's index to its placed part; `extra` are placed parts matching none (or a
 * duplicate), which a catalogue update removes.
 */
export function matchParts<T extends { name: string; partId?: string }>(
  insts: T[], parts: Array<{ id: string; name: string }>,
): { matched: Map<number, T>; extra: T[] } {
  const matched = new Map<number, T>();
  const extra: T[] = [];
  const byId = new Map(parts.map((p, i) => [p.id, i]));
  const rest: T[] = [];
  for (const inst of insts) {
    const i = inst.partId !== undefined ? byId.get(inst.partId) : undefined;
    if (i !== undefined && !matched.has(i)) matched.set(i, inst);
    else if (inst.partId !== undefined && i !== undefined) extra.push(inst); // a duplicate
    else rest.push(inst);
  }
  for (const inst of rest) {
    const i = parts.findIndex((p, k) => p.name === inst.name && !matched.has(k));
    if (i >= 0) matched.set(i, inst); else extra.push(inst);
  }
  return { matched, extra };
}

/**
 * Load-time: a group saved before products were linked groups is linked to its
 * catalogue product when all its parts are parts of ONE multi-part product
 * (`partOf` names the product and the part, or null), and each part learns its
 * `partId`. The automatic "Group NN" name it was given goes: a product is shown
 * by its own name.
 */
export function linkProductGroups(d: Drawing, partOf: (i: ProductInstance) => { templateId: string; partId?: string } | null): void {
  for (const g of d.groups || []) {
    if (g.source) continue;
    const members = (d.instances || []).filter((i) => i.groupId === g.id);
    const objs = groupables(d).filter((o) => o.groupId === g.id);
    if (!members.length || members.length !== objs.length) continue;
    const info = members.map(partOf);
    const tid = info[0]?.templateId;
    if (!tid || info.some((x) => x?.templateId !== tid)) continue;
    g.source = { templateId: tid };
    // Products were named "Group NN" automatically; a product shows its own name.
    if (g.name && isAutoGroupName(g.name)) delete g.name;
    members.forEach((m, k) => { if (info[k]?.partId && m.partId === undefined) m.partId = info[k]!.partId; });
  }
}
